import { useEffect, useMemo, useState } from "react";
import { Lightbulb, Hourglass, Flame, TrendingUp, Zap, Trophy, User, Users, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const ICONS = { hourglass: Hourglass, flame: Flame, trending: TrendingUp, zap: Zap, trophy: Trophy, user: User, users: Users, tip: Sparkles };
const TONES = {
  amber: "from-amber-50 to-white border-amber-200 text-amber-700",
  orange: "from-orange-50 to-white border-orange-200 text-orange-600",
  emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
  blue: "from-blue-50 to-white border-blue-200 text-blue-700",
  violet: "from-violet-50 to-white border-violet-200 text-violet-700",
  slate: "from-slate-50 to-white border-slate-200 text-slate-600",
};

// Feature tips mixed in with the data facts, so new users discover features.
const TIPS = [
  "Reply **done 12** in your Brainydo WhatsApp group to close task #12 from your phone.",
  "Press **⌘K** (Ctrl K) anywhere to jump to any task, chat or person.",
  "**Focus mode** walks you through overdue tasks one at a time — D for done, S to snooze.",
  "Snooze a task to **tomorrow morning** in one tap from its row on the Tasks page.",
  "Ask Brainydo things like **“what did Rajesh ask for this week?”**",
  "Only want some chats read? Settings → **Only selected chats**.",
  "Follow your busy groups under **What's buzzing** and get highlight cards every 2 hours.",
];

const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
  p.startsWith("**") ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong> : p);

export default function DidYouKnow({ facts = [] }) {
  const items = useMemo(() => {
    const tips = [...TIPS].sort(() => Math.random() - 0.5).slice(0, 3).map((text) => ({ icon: "tip", tone: "blue", text }));
    // Interleave: fact, fact, tip, fact, tip…
    const out = [];
    const f = [...facts];
    while (f.length || tips.length) {
      if (f.length) out.push(f.shift());
      if (f.length) out.push(f.shift());
      if (tips.length) out.push(tips.shift());
    }
    return out;
  }, [facts]);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const id = setInterval(() => setI((x) => (x + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [paused, items.length]);

  if (!items.length) return null;
  const cur = items[i % items.length];
  const Icon = ICONS[cur.icon] || Lightbulb;
  const tone = TONES[cur.tone] || TONES.slate;
  const go = (d) => setI((x) => (x + d + items.length) % items.length);

  return (
    <section className={`relative rounded-xl border bg-gradient-to-br p-4 overflow-hidden ${tone}`}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="did-you-know">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] flex items-center gap-1.5">
          <Lightbulb size={12} /> Did you know
        </span>
        <div className="flex items-center gap-0.5 text-slate-400">
          <button onClick={() => go(-1)} className="h-6 w-6 grid place-items-center rounded hover:bg-white/80 hover:text-slate-700" aria-label="Previous"><ChevronLeft size={14} /></button>
          <button onClick={() => go(1)} className="h-6 w-6 grid place-items-center rounded hover:bg-white/80 hover:text-slate-700" aria-label="Next"><ChevronRight size={14} /></button>
        </div>
      </div>
      <div key={i} className="flex items-start gap-3 min-h-[48px] sw-slide-in-right">
        <div className="w-9 h-9 rounded-lg bg-white/90 border border-white shadow-sm grid place-items-center shrink-0"><Icon size={17} /></div>
        <p className="text-[13.5px] leading-snug text-slate-700">{bold(cur.text)}</p>
      </div>
      <div className="flex gap-1 mt-3">
        {items.map((_, k) => (
          <button key={k} onClick={() => setI(k)} aria-label={`Fact ${k + 1}`}
            className={"h-1 rounded-full transition-all duration-300 " + (k === i % items.length ? "w-5 bg-current" : "w-1.5 bg-slate-300 hover:bg-slate-400")} />
        ))}
      </div>
    </section>
  );
}
